import React, { useEffect } from "react";
import { segmentList } from "@ofqual/portal-core/core/utils/listHelpers";
import { formatAsTodayYesterdayOrDate } from "@ofqual/portal-core/core/utils/dateUtils";
import { ActivitySegmentHeader } from "./ActivitySegmentHeader";
import { ActivityFeedItem } from "./ActivityFeedItem";
import { ActivityFeedItemDefinition } from "../domain/ActivityFeedItemDefinition";
import { useDispatch } from "react-redux";
import { loadingActivities, loadingThreads } from "../redux/actions";
import { defaultActivityFeedState } from "../redux/reducers";
import { useActivityFeedState } from "../hooks/useActivityFeedState";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { useScrollToBottomRef } from "@ofqual/portal-core/core/hooks";
import { FilterMenuProps } from "@ofqual/portal-components/components/ResponseFilter/FilterMenu";
import {
  ScrollableAreaTabFooter,
  ScrollableAreaTabAction,
} from "@ofqual/portal-components/components/ScrollableAreaTabFooter";
import {
  ScrollableAreaTab,
  ScrollableAreaTabSkeleton,
} from "@ofqual/portal-components/components/ScrollableAreaTab";

import { Activity } from "../domain/Activity";
import { ActivityType } from "../domain/ActivityType";

export type ActivityFeedProps = {
  resourceId: string | null | undefined;
  params?: QueryParams;
  definitions: ActivityFeedItemDefinition<any>[];
  actions: ScrollableAreaTabAction[];
  activeFilters?: number;
  children?: FilterMenuProps["children"];
  specialSegmentLabel?: (activity: Activity) => string | null;
  customLoading?: boolean;
};

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  resourceId,
  definitions,
  actions,
  params: inputParams,
  activeFilters,
  children,
  customLoading,
  specialSegmentLabel = (activity) => null,
}) => {

  const threadedMessageActivities = [
    ActivityType.SpecialistOfqualSendPublicMessage,
    ActivityType.SpecialistCitizenSendPublicMessage
  ];

  const dispatch = useDispatch();
  useEffect(() => {
    if (resourceId && !customLoading) {
      if (inputParams && inputParams?.activityTypes !== undefined) {
        if (threadedMessageActivities.every((r) => inputParams["activityTypes"].includes(r))) {
          dispatch(loadingThreads(resourceId));
        } else {
          dispatch(loadingActivities(resourceId, inputParams));
        }
      }
      else {
        dispatch(loadingActivities(resourceId, inputParams));
      }
    }
  }, [dispatch, resourceId, inputParams]);

  const {
    status,
    params,
    response: { data },
  } = useActivityFeedState(resourceId) || defaultActivityFeedState;

  const scrollRef = useScrollToBottomRef<HTMLDivElement>(data);

  const footer =
    actions.length || children ? (
      <ScrollableAreaTabFooter
        params={params}
        actions={actions}
        activeFilters={activeFilters}
        navigate={
          children
            ? (newParams) => {
              if (resourceId) {
                if (newParams && newParams?.activityTypes !== undefined) {
                  if (threadedMessageActivities.every((r) => newParams["activityTypes"].includes(r))) {
                    dispatch(loadingThreads(resourceId))
                  }
                }
                else {
                  dispatch(loadingActivities(resourceId, newParams))
                }
              }
            }
            : undefined
        }
      >
        {children}
      </ScrollableAreaTabFooter>
    ) : null;

  if (status === "LOADING") {
    return (
      <>
        <ScrollableAreaTabSkeleton hasActions={!!actions.length} />
        {footer}
      </>
    );
  }

  const segmentedActivities = segmentList(
    data,
    (item) =>
      // if there has been a specific function passed to establish labelling, use that.
      //  note: specialSegmentLabel should return null for any activites that don't qualify
      //  to be specially labelled
      specialSegmentLabel(item) ||
      /// ... fall back to a standard by-date segmentation.
      formatAsTodayYesterdayOrDate(item.createdDate) ||
      ""
  );

  // netters ok I am changing it so a filter is treated as an action
  const hasActionsFilter = !!actions.length || children ? true : false;
  return (
    <>
      <ScrollableAreaTab hasActions={hasActionsFilter} ref={scrollRef}>
        {segmentedActivities.map((segment) => (
          <div key={segment.label}>
            <ActivitySegmentHeader label={segment.label} />
            {segment.data.map((item) => (
              <ActivityFeedItem
                key={item.id}
                activity={item}
                definition={definitions.find(
                  (definition) => definition.activityType === item.activityType
                )}
              />
            ))}
          </div>
        ))}
      </ScrollableAreaTab>
      {footer}
    </>
  );
};
